import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { SOCIAL_NETWORKS, ICON_STYLES, getSocialIcon } from '../../utils/socialNetworks';
import MarginControl from './MarginControl';
import ShortcodeSelector from './ShortcodeSelector';
import { getElementTypeLabel } from '../../utils/translations';
import IconPicker from '../builder/IconPicker';
import { getIconApiUrl } from '../../utils/iconUtils';
import MediaLibraryModal from '../media/MediaLibraryModal';
import ColorPickerWithPresets from '../common/ColorPickerWithPresets';
import config from '../../config';

function PropertiesPanel({ element, onUpdate, onImageUpload, apiEnabled, showNotification }) {
  const [quillValue, setQuillValue] = useState(element.value || '');
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const textInputRef = useRef(null);
  const iconTextInputRef = useRef(null);
  const buttonTextInputRef = useRef(null);

  // Fonction pour insérer un shortcode dans un input
  const insertShortcode = (shortcode, inputRef, fieldName) => {
    if (inputRef.current) {
      const input = inputRef.current;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const currentValue = element[fieldName] || '';

      const newValue =
        currentValue.substring(0, start) +
        shortcode +
        currentValue.substring(end);

      onUpdate({ [fieldName]: newValue });

      // Remettre le focus et positionner le curseur après le shortcode
      setTimeout(() => {
        input.focus();
        const newPosition = start + shortcode.length;
        input.setSelectionRange(newPosition, newPosition);
      }, 0);
    }
  };

  useEffect(() => {
    if (element.type === 'richtext') {
      setQuillValue(element.value || '');
    }
  }, [element]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const url = await onImageUpload(file);
    if (url) {
      onUpdate({ value: url });
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ]
  };

  const renderProperties = () => {
    switch (element.type) {
      case 'text':
        return (
          <>
            <div className="form-group">
              <label>Texte</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <input
                  ref={textInputRef}
                  type="text"
                  value={element.value}
                  onChange={(e) => onUpdate({ value: e.target.value })}
                  style={{ flex: 1 }}
                />
                <ShortcodeSelector
                  onSelect={(shortcode) => insertShortcode(shortcode, textInputRef, 'value')}
                  inputRef={textInputRef}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Lien (optionnel)</label>
              <input
                type="text"
                value={element.link || ''}
                onChange={(e) => onUpdate({ link: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <ColorPickerWithPresets
                label="Couleur"
                value={element.style?.color || '#000000'}
                onChange={(color) => onUpdate({ style: { ...element.style, color } })}
              />
            </div>
            <div className="form-group">
              <label>Taille</label>
              <select
                value={element.style?.fontSize || '14px'}
                onChange={(e) => onUpdate({ style: { ...element.style, fontSize: e.target.value } })}
              >
                {['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Police</label>
              <select
                value={element.style?.fontFamily || 'Arial, sans-serif'}
                onChange={(e) => onUpdate({ style: { ...element.style, fontFamily: e.target.value } })}
              >
                <option value="Arial, sans-serif">Arial</option>
                <option value="Helvetica, sans-serif">Helvetica</option>
                <option value="'Times New Roman', serif">Times New Roman</option>
                <option value="Georgia, serif">Georgia</option>
                <option value="'Courier New', monospace">Courier New</option>
                <option value="Verdana, sans-serif">Verdana</option>
                <option value="Tahoma, sans-serif">Tahoma</option>
                <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
              </select>
            </div>
            <div className="form-group">
              <label>Alignement</label>
              <div className="alignment-buttons">
                <button
                  type="button"
                  className={`align-btn ${(element.style?.textAlign || 'left') === 'left' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, textAlign: 'left' } })}
                  title="Aligner à gauche"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="2" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="2" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className={`align-btn ${(element.style?.textAlign || 'left') === 'center' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, textAlign: 'center' } })}
                  title="Centrer"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="4" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="5" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className={`align-btn ${(element.style?.textAlign || 'left') === 'right' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, textAlign: 'right' } })}
                  title="Aligner à droite"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="6" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="8" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="form-group">
              <label>Mise en forme</label>
              <div className="text-format-buttons">
                <button
                  type="button"
                  className={`format-btn ${element.style?.fontWeight === 'bold' ? 'active' : ''}`}
                  onClick={() => onUpdate({
                    style: {
                      ...element.style,
                      fontWeight: element.style?.fontWeight === 'bold' ? 'normal' : 'bold'
                    }
                  })}
                  title="Gras"
                >
                  <strong>B</strong>
                </button>
                <button
                  type="button"
                  className={`format-btn ${element.style?.fontStyle === 'italic' ? 'active' : ''}`}
                  onClick={() => onUpdate({
                    style: {
                      ...element.style,
                      fontStyle: element.style?.fontStyle === 'italic' ? 'normal' : 'italic'
                    }
                  })}
                  title="Italique"
                >
                  <em>I</em>
                </button>
                <button
                  type="button"
                  className={`format-btn ${element.style?.textDecoration?.includes('underline') ? 'active' : ''}`}
                  onClick={() => onUpdate({
                    style: {
                      ...element.style,
                      textDecoration: element.style?.textDecoration?.includes('underline') ? 'none' : 'underline'
                    }
                  })}
                  title="Souligné"
                >
                  <u>U</u>
                </button>
              </div>
            </div>
            <MarginControl
              margins={{
                marginTop: element.style?.marginTop,
                marginRight: element.style?.marginRight,
                marginBottom: element.style?.marginBottom,
                marginLeft: element.style?.marginLeft
              }}
              onChange={(margins) => onUpdate({ style: { ...element.style, ...margins } })}
            />
          </>
        );

      case 'richtext':
        return (
          <>
            <div className="form-group">
              <label>Éditeur de texte enrichi</label>
              <div className="quill-editor-container">
                <ReactQuill
                  theme="snow"
                  value={quillValue}
                  onChange={(content) => {
                    setQuillValue(content);
                    onUpdate({ value: content });
                  }}
                  modules={modules}
                />
              </div>
            </div>
            <MarginControl
              margins={{
                marginTop: element.style?.marginTop,
                marginRight: element.style?.marginRight,
                marginBottom: element.style?.marginBottom,
                marginLeft: element.style?.marginLeft
              }}
              onChange={(margins) => onUpdate({ style: { ...element.style, ...margins } })}
            />
          </>
        );

      case 'image':
        // Vérifier si une image valide est sélectionnée (URL complète HTTP)
        const hasValidImage = element.value &&
                              typeof element.value === 'string' &&
                              element.value.trim() !== '' &&
                              (element.value.startsWith('http://') || element.value.startsWith('https://') || element.value.startsWith('/'));
        // Utiliser le texte alternatif si disponible, sinon le nom de fichier de l'URL
        const imageDisplayName = element.alt || (element.value ? element.value.split('/').pop() : null);

        return (
          <>
            {/* Aperçu de l'image sélectionnée */}
            {hasValidImage && (
              <div className="form-group">
                <label>Image sélectionnée</label>
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '12px',
                  background: '#f9fafb'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '8px'
                  }}>
                    <img
                      src={element.value}
                      alt="Aperçu"
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'contain',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        background: 'white'
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: '#374151',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {imageDisplayName}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        marginTop: '2px'
                      }}>
                        {element.style?.width || 'Auto'} × {element.style?.height || 'Auto'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bouton bibliothèque de médias */}
            {apiEnabled && (
              <div className="form-group">
                <button
                  type="button"
                  onClick={() => setShowMediaLibrary(true)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  📁 {hasValidImage ? 'Changer l\'image' : 'Sélectionner une image'}
                </button>
              </div>
            )}
            <div className="form-group">
              <label>Lien (optionnel)</label>
              <input
                type="text"
                value={element.link || ''}
                onChange={(e) => onUpdate({ link: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <label>Largeur</label>
              <input
                type="text"
                value={element.style?.width || '200px'}
                onChange={(e) => onUpdate({ style: { ...element.style, width: e.target.value } })}
                placeholder="200px"
              />
            </div>
            <div className="form-group">
              <label>Hauteur (0 = auto)</label>
              <input
                type="text"
                value={element.style?.height || '0'}
                onChange={(e) => onUpdate({ style: { ...element.style, height: e.target.value } })}
                placeholder="0, auto, ou 150px"
              />
            </div>
            <div className="form-group">
              <label>Format</label>
              <div className="image-shape-buttons">
                <button
                  type="button"
                  className={`shape-btn ${(element.style?.imageShape || 'square') === 'square' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, imageShape: 'square' } })}
                  title="Carré"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="4" y="4" width="16" height="16" rx="0" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`shape-btn ${(element.style?.imageShape) === 'rounded' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, imageShape: 'rounded' } })}
                  title="Arrondi"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="4" y="4" width="16" height="16" rx="4" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`shape-btn ${(element.style?.imageShape) === 'circle' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, imageShape: 'circle' } })}
                  title="Cercle"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="8" />
                  </svg>
                </button>
              </div>
            </div>
            {element.style?.imageShape === 'rounded' && (
              <div className="form-group">
                <label>Rayon des coins</label>
                <input
                  type="text"
                  value={element.style?.borderRadius || '8px'}
                  onChange={(e) => onUpdate({ style: { ...element.style, borderRadius: e.target.value } })}
                  placeholder="8px"
                />
              </div>
            )}
            <div className="form-group">
              <label>Alignement</label>
              <div className="alignment-buttons">
                <button
                  type="button"
                  className={`align-btn ${(element.style?.textAlign || 'left') === 'left' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, textAlign: 'left' } })}
                  title="Aligner à gauche"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="2" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="2" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className={`align-btn ${(element.style?.textAlign || 'left') === 'center' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, textAlign: 'center' } })}
                  title="Centrer"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="4" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="5" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className={`align-btn ${(element.style?.textAlign || 'left') === 'right' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, textAlign: 'right' } })}
                  title="Aligner à droite"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="6" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="8" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
              </div>
            </div>
            <MarginControl
              margins={{
                marginTop: element.style?.marginTop,
                marginRight: element.style?.marginRight,
                marginBottom: element.style?.marginBottom,
                marginLeft: element.style?.marginLeft
              }}
              onChange={(margins) => onUpdate({ style: { ...element.style, ...margins } })}
            />
          </>
        );

      case 'profilephoto':
        return (
          <>
            <div className="form-group">
              <div style={{
                padding: '12px',
                background: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '6px',
                marginBottom: '16px'
              }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#0369a1' }}>
                  👤 La photo de profil sera automatiquement remplacée par celle de l'utilisateur lors de l'affichage de la signature.
                </p>
              </div>
            </div>
            <div className="form-group">
              <label>Taille</label>
              <input
                type="text"
                value={element.style?.width || '100px'}
                onChange={(e) => onUpdate({ style: { ...element.style, width: e.target.value, height: e.target.value } })}
                placeholder="100px"
              />
            </div>
            <div className="form-group">
              <label>Format</label>
              <div className="image-shape-buttons">
                <button
                  type="button"
                  className={`shape-btn ${(element.style?.imageShape || 'circle') === 'square' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, imageShape: 'square' } })}
                  title="Carré"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="4" y="4" width="16" height="16" rx="0" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`shape-btn ${(element.style?.imageShape) === 'rounded' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, imageShape: 'rounded' } })}
                  title="Arrondi"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="4" y="4" width="16" height="16" rx="4" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`shape-btn ${(element.style?.imageShape || 'circle') === 'circle' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, imageShape: 'circle' } })}
                  title="Cercle"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="8" />
                  </svg>
                </button>
              </div>
            </div>
            {element.style?.imageShape === 'rounded' && (
              <div className="form-group">
                <label>Rayon des coins</label>
                <input
                  type="text"
                  value={element.style?.borderRadius || '8px'}
                  onChange={(e) => onUpdate({ style: { ...element.style, borderRadius: e.target.value } })}
                  placeholder="8px"
                />
              </div>
            )}
            <div className="form-group">
              <label>Alignement</label>
              <div className="alignment-buttons">
                <button
                  type="button"
                  className={`align-btn ${(element.style?.textAlign || 'left') === 'left' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, textAlign: 'left' } })}
                  title="Aligner à gauche"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="2" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="2" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className={`align-btn ${(element.style?.textAlign || 'left') === 'center' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, textAlign: 'center' } })}
                  title="Centrer"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="4" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="5" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className={`align-btn ${(element.style?.textAlign || 'left') === 'right' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, textAlign: 'right' } })}
                  title="Aligner à droite"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="6" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="8" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
              </div>
            </div>
            <MarginControl
              margins={{
                marginTop: element.style?.marginTop,
                marginRight: element.style?.marginRight,
                marginBottom: element.style?.marginBottom,
                marginLeft: element.style?.marginLeft
              }}
              onChange={(margins) => onUpdate({ style: { ...element.style, ...margins } })}
            />
          </>
        );

      case 'orglogo':
        return (
          <>
            <div className="form-group">
              <div style={{
                padding: '12px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '6px',
                marginBottom: '16px'
              }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#166534' }}>
                  Le logo de l'organisation sera automatiquement utilisé depuis les paramètres de l'organisation.
                </p>
              </div>
            </div>
            <div className="form-group">
              <label>Largeur</label>
              <input
                type="text"
                value={element.style?.width || '150px'}
                onChange={(e) => onUpdate({ style: { ...element.style, width: e.target.value } })}
                placeholder="150px"
              />
            </div>
            <div className="form-group">
              <label>Hauteur</label>
              <input
                type="text"
                value={element.style?.height || 'auto'}
                onChange={(e) => onUpdate({ style: { ...element.style, height: e.target.value } })}
                placeholder="auto"
              />
            </div>
            <div className="form-group">
              <label>Alignement</label>
              <div className="alignment-buttons">
                <button
                  type="button"
                  className={`align-btn ${(element.style?.textAlign || 'left') === 'left' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, textAlign: 'left' } })}
                  title="Aligner à gauche"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="2" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="2" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className={`align-btn ${(element.style?.textAlign || 'left') === 'center' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, textAlign: 'center' } })}
                  title="Centrer"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="4" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="5" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className={`align-btn ${(element.style?.textAlign || 'left') === 'right' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, textAlign: 'right' } })}
                  title="Aligner à droite"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="6" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="8" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
              </div>
            </div>
            <MarginControl
              margins={{
                marginTop: element.style?.marginTop,
                marginRight: element.style?.marginRight,
                marginBottom: element.style?.marginBottom,
                marginLeft: element.style?.marginLeft
              }}
              onChange={(margins) => onUpdate({ style: { ...element.style, ...margins } })}
            />
          </>
        );

      case 'banner':
        // Vérifier si une image valide est sélectionnée (URL complète HTTP)
        const hasValidBanner = element.value &&
                               typeof element.value === 'string' &&
                               element.value.trim() !== '' &&
                               (element.value.startsWith('http://') || element.value.startsWith('https://') || element.value.startsWith('/'));
        // Utiliser le texte alternatif si disponible, sinon le nom de fichier de l'URL
        const bannerDisplayName = element.alt || (element.value ? element.value.split('/').pop() : null);

        return (
          <>
            {/* Aperçu de l'image sélectionnée */}
            {hasValidBanner && (
              <div className="form-group">
                <label>Image sélectionnée</label>
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '12px',
                  background: '#f9fafb'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '8px'
                  }}>
                    <img
                      src={element.value}
                      alt="Aperçu"
                      style={{
                        width: '80px',
                        height: '50px',
                        objectFit: 'cover',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        background: 'white'
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: '#374151',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {bannerDisplayName}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        marginTop: '2px'
                      }}>
                        Hauteur: {element.style?.height || '150px'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bouton bibliothèque de médias */}
            {apiEnabled && (
              <div className="form-group">
                <button
                  type="button"
                  onClick={() => setShowMediaLibrary(true)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  📁 {hasValidBanner ? 'Changer l\'image' : 'Sélectionner une image'}
                </button>
              </div>
            )}
            <div className="form-group">
              <label>Hauteur</label>
              <input
                type="text"
                value={element.style?.height || '150px'}
                onChange={(e) => onUpdate({ style: { ...element.style, height: e.target.value } })}
                placeholder="150px"
              />
            </div>
            <div className="form-group">
              <label>Lien</label>
              <input
                type="text"
                value={element.link || ''}
                onChange={(e) => onUpdate({ link: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <MarginControl
              margins={{
                marginTop: element.style?.marginTop,
                marginRight: element.style?.marginRight,
                marginBottom: element.style?.marginBottom,
                marginLeft: element.style?.marginLeft
              }}
              onChange={(margins) => onUpdate({ style: { ...element.style, ...margins } })}
            />
          </>
        );

      case 'button':
        return (
          <>
            <div className="form-group">
              <label>Texte</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <input
                  ref={buttonTextInputRef}
                  type="text"
                  value={element.value}
                  onChange={(e) => onUpdate({ value: e.target.value })}
                  style={{ flex: 1 }}
                />
                <ShortcodeSelector
                  onSelect={(shortcode) => insertShortcode(shortcode, buttonTextInputRef, 'value')}
                  inputRef={buttonTextInputRef}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Lien</label>
              <input
                type="text"
                value={element.link || ''}
                onChange={(e) => onUpdate({ link: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <ColorPickerWithPresets
                label="Couleur du texte"
                value={element.style?.color || '#ffffff'}
                onChange={(color) => onUpdate({ style: { ...element.style, color } })}
              />
            </div>
            <div className="form-group">
              <ColorPickerWithPresets
                label="Couleur de fond"
                value={element.style?.backgroundColor || '#3498db'}
                onChange={(color) => onUpdate({ style: { ...element.style, backgroundColor: color } })}
              />
            </div>
            <div className="form-group">
              <label>Padding</label>
              <input
                type="text"
                value={element.style?.padding || '12px 24px'}
                onChange={(e) => onUpdate({ style: { ...element.style, padding: e.target.value } })}
                placeholder="12px 24px"
              />
            </div>
            <div className="form-group">
              <label>Bordure arrondie</label>
              <input
                type="text"
                value={element.style?.borderRadius || '6px'}
                onChange={(e) => onUpdate({ style: { ...element.style, borderRadius: e.target.value } })}
                placeholder="6px"
              />
            </div>
            <MarginControl
              margins={{
                marginTop: element.style?.marginTop,
                marginRight: element.style?.marginRight,
                marginBottom: element.style?.marginBottom,
                marginLeft: element.style?.marginLeft
              }}
              onChange={(margins) => onUpdate({ style: { ...element.style, ...margins } })}
            />
          </>
        );

      case 'icontext':
        const iconType = element.iconType || 'emoji';
        return (
          <>
            <div className="form-group">
              <label>Type d'icône</label>
              <select
                value={iconType}
                onChange={(e) => onUpdate({ iconType: e.target.value })}
              >
                <option value="emoji">Emoji</option>
                <option value="library">Bibliothèque d'icônes</option>
                <option value="image">Image (SVG/PNG)</option>
              </select>
            </div>

            {iconType === 'emoji' ? (
              <div className="form-group">
                <label>Icône (emoji)</label>
                <input
                  type="text"
                  value={element.icon || '📧'}
                  onChange={(e) => onUpdate({ icon: e.target.value })}
                  placeholder="📧"
                />
              </div>
            ) : iconType === 'library' ? (
              <div className="form-group">
                <label>Icône de la bibliothèque</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {element.libraryIcon && (
                    <div style={{
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      background: '#f9fafb'
                    }}>
                      <img
                        src={getIconApiUrl(
                          element.libraryIcon,
                          element.style?.iconColor || element.style?.color || '#000000',
                          24
                        )}
                        alt={element.libraryIcon.name}
                        style={{
                          width: '24px',
                          height: '24px',
                          display: 'block'
                        }}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowIconPicker(true)}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#2563eb'}
                    onMouseOut={(e) => e.target.style.background = '#3b82f6'}
                  >
                    {element.libraryIcon ? 'Changer l\'icône' : 'Sélectionner une icône'}
                  </button>
                </div>
                {element.libraryIcon && (
                  <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    {element.libraryIcon.name} ({element.libraryIcon.style})
                  </small>
                )}
              </div>
            ) : (
              <div className="form-group">
                <label>URL de l'image</label>
                <input
                  type="text"
                  value={element.iconUrl || ''}
                  onChange={(e) => onUpdate({ iconUrl: e.target.value })}
                  placeholder="https://example.com/icon.svg"
                />
              </div>
            )}

            <div className="form-group">
              <label>Texte</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <input
                  ref={iconTextInputRef}
                  type="text"
                  value={element.text || element.value}
                  onChange={(e) => onUpdate({ text: e.target.value })}
                  style={{ flex: 1 }}
                />
                <ShortcodeSelector
                  onSelect={(shortcode) => insertShortcode(shortcode, iconTextInputRef, 'text')}
                  inputRef={iconTextInputRef}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Lien (optionnel)</label>
              <input
                type="text"
                value={element.link || ''}
                onChange={(e) => onUpdate({ link: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <ColorPickerWithPresets
                label="Couleur du texte"
                value={element.style?.color || '#000000'}
                onChange={(color) => onUpdate({ style: { ...element.style, color } })}
              />
            </div>
            <div className="form-group">
              <ColorPickerWithPresets
                label="Couleur de l'icône"
                value={element.style?.iconColor || element.style?.color || '#000000'}
                onChange={(color) => onUpdate({ style: { ...element.style, iconColor: color } })}
              />
            </div>
            <div className="form-group">
              <label>Taille du texte</label>
              <select
                value={element.style?.fontSize || '14px'}
                onChange={(e) => onUpdate({ style: { ...element.style, fontSize: e.target.value } })}
              >
                {['12px', '14px', '16px', '18px', '20px'].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Taille de l'icône</label>
              <select
                value={element.style?.iconSize || '20px'}
                onChange={(e) => onUpdate({ style: { ...element.style, iconSize: e.target.value } })}
              >
                {['16px', '20px', '24px', '28px', '32px'].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <MarginControl
              margins={{
                marginTop: element.style?.marginTop,
                marginRight: element.style?.marginRight,
                marginBottom: element.style?.marginBottom,
                marginLeft: element.style?.marginLeft
              }}
              onChange={(margins) => onUpdate({ style: { ...element.style, ...margins } })}
            />
          </>
        );

      case 'divider':
        return (
          <>
            <div className="form-group">
              <ColorPickerWithPresets
                label="Couleur"
                value={element.style?.color || '#e0e0e0'}
                onChange={(color) => onUpdate({ style: { ...element.style, color } })}
              />
            </div>
            <div className="form-group">
              <label>Épaisseur</label>
              <select
                value={element.style?.height || '1px'}
                onChange={(e) => onUpdate({ style: { ...element.style, height: e.target.value } })}
              >
                {['1px', '2px', '3px', '4px', '5px'].map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Style</label>
              <select
                value={element.style?.borderStyle || 'solid'}
                onChange={(e) => onUpdate({ style: { ...element.style, borderStyle: e.target.value } })}
              >
                <option value="solid">Solide</option>
                <option value="dashed">Tirets</option>
                <option value="dotted">Pointillé</option>
              </select>
            </div>
            <MarginControl
              margins={{
                marginTop: element.style?.marginTop,
                marginRight: element.style?.marginRight,
                marginBottom: element.style?.marginBottom,
                marginLeft: element.style?.marginLeft
              }}
              onChange={(margins) => onUpdate({ style: { ...element.style, ...margins } })}
            />
          </>
        );

      case 'spacer':
        return (
          <>
            <div className="form-group">
              <label>Hauteur</label>
              <input
                type="text"
                value={element.style?.height || '20px'}
                onChange={(e) => onUpdate({ style: { ...element.style, height: e.target.value } })}
                placeholder="20px"
              />
            </div>
            <MarginControl
              margins={{
                marginTop: element.style?.marginTop,
                marginRight: element.style?.marginRight,
                marginBottom: element.style?.marginBottom,
                marginLeft: element.style?.marginLeft
              }}
              onChange={(margins) => onUpdate({ style: { ...element.style, ...margins } })}
            />
          </>
        );

      case 'social':
        return (
          <>
            <div className="form-group">
              <label>Réseau social</label>
              <select
                value={element.network || 'linkedin'}
                onChange={(e) => onUpdate({ network: e.target.value })}
              >
                {Object.entries(SOCIAL_NETWORKS).map(([key, net]) => (
                  <option key={key} value={key}>{net.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Lien</label>
              <input
                type="text"
                value={element.link || ''}
                onChange={(e) => onUpdate({ link: e.target.value })}
                placeholder={SOCIAL_NETWORKS[element.network]?.placeholder || 'https://...'}
              />
            </div>
            <div className="form-group">
              <label>Taille</label>
              <select
                value={element.style?.size || '32px'}
                onChange={(e) => onUpdate({ style: { ...element.style, size: e.target.value } })}
              >
                {['24px', '32px', '40px', '48px'].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Style d'icône</label>
              <select
                value={element.style?.iconStyle || 'flat'}
                onChange={(e) => onUpdate({ style: { ...element.style, iconStyle: e.target.value } })}
              >
                {Object.entries(ICON_STYLES).map(([key, style]) => (
                  <option key={key} value={key}>{style.label}</option>
                ))}
              </select>
            </div>
            <MarginControl
              margins={{
                marginTop: element.style?.marginTop,
                marginRight: element.style?.marginRight,
                marginBottom: element.style?.marginBottom,
                marginLeft: element.style?.marginLeft
              }}
              onChange={(margins) => onUpdate({ style: { ...element.style, ...margins } })}
            />
          </>
        );

      case 'socialgroup':
        const selectedNetworks = element.networks || [];
        const availableNetworks = Object.keys(SOCIAL_NETWORKS).filter(key => !selectedNetworks.includes(key));
        const currentIconStyle = element.style?.iconStyle || 'flat';

        const moveNetwork = (index, direction) => {
          const newNetworks = [...selectedNetworks];
          const newIndex = direction === 'up' ? index - 1 : index + 1;
          if (newIndex < 0 || newIndex >= newNetworks.length) return;
          [newNetworks[index], newNetworks[newIndex]] = [newNetworks[newIndex], newNetworks[index]];
          onUpdate({ networks: newNetworks });
        };

        const removeNetwork = (key) => {
          onUpdate({ networks: selectedNetworks.filter(n => n !== key) });
        };

        const addNetwork = (key) => {
          onUpdate({ networks: [...selectedNetworks, key] });
        };

        return (
          <>
            <div className="form-group">
              <label>Réseaux sélectionnés ({selectedNetworks.length})</label>
              {selectedNetworks.length === 0 ? (
                <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '6px', textAlign: 'center', color: '#7f8c8d', fontSize: '13px' }}>
                  Aucun réseau social sélectionné. Ajoutez-en ci-dessous.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedNetworks.map((key, index) => {
                    const net = SOCIAL_NETWORKS[key];
                    if (!net) return null;
                    return (
                      <div key={key} style={{
                        padding: '12px',
                        background: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <img src={getSocialIcon(key, currentIconStyle)} alt={net.name} style={{ width: '24px', height: '24px' }} />
                          <span style={{ fontWeight: '500', flex: 1 }}>{net.name}</span>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {index > 0 && (
                              <button
                                type="button"
                                onClick={() => moveNetwork(index, 'up')}
                                className="social-order-btn"
                                title="Déplacer vers le haut"
                              >
                                ↑
                              </button>
                            )}
                            {index < selectedNetworks.length - 1 && (
                              <button
                                type="button"
                                onClick={() => moveNetwork(index, 'down')}
                                className="social-order-btn"
                                title="Déplacer vers le bas"
                              >
                                ↓
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeNetwork(key)}
                              className="social-remove-btn"
                              title="Supprimer"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                        <input
                          type="text"
                          value={element.links?.[key] || ''}
                          onChange={(e) => onUpdate({ links: { ...element.links, [key]: e.target.value } })}
                          placeholder={`URL ${net.name}`}
                          style={{ width: '100%' }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {availableNetworks.length > 0 && (
              <div className="form-group">
                <label>Ajouter un réseau</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
                  {availableNetworks.map(key => {
                    const net = SOCIAL_NETWORKS[key];
                    if (!net) return null;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => addNetwork(key)}
                        className="social-add-btn"
                      >
                        <img src={getSocialIcon(key, currentIconStyle)} alt={net.name} style={{ width: '20px', height: '20px' }} />
                        <span>{net.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Style d'icônes</label>
              <select
                value={currentIconStyle}
                onChange={(e) => onUpdate({ style: { ...element.style, iconStyle: e.target.value } })}
              >
                {Object.entries(ICON_STYLES).map(([key, style]) => (
                  <option key={key} value={key}>{style.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Taille des icônes</label>
              <input
                type="text"
                value={element.style?.size || '32px'}
                onChange={(e) => onUpdate({ style: { ...element.style, size: e.target.value } })}
                onKeyDown={(e) => {
                  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                  e.preventDefault();

                  const currentValue = element.style?.size || '32px';
                  const match = currentValue.match(/^(-?\d*\.?\d+)(.*)$/);

                  if (!match) {
                    const newValue = e.key === 'ArrowUp' ? '1px' : '0px';
                    onUpdate({ style: { ...element.style, size: newValue } });
                    return;
                  }

                  const numValue = parseFloat(match[1]) || 0;
                  const unit = match[2] || 'px';
                  let increment = 1;
                  if (e.shiftKey) increment = 10;
                  else if (e.altKey) increment = 0.1;

                  const newValue = e.key === 'ArrowUp' ? numValue + increment : numValue - increment;
                  const roundedValue = e.altKey ? newValue.toFixed(1) : Math.round(newValue);
                  onUpdate({ style: { ...element.style, size: `${roundedValue}${unit}` } });
                }}
                placeholder="32px"
              />
            </div>
            <div className="form-group">
              <label>Espacement</label>
              <input
                type="text"
                value={element.style?.spacing || '8px'}
                onChange={(e) => onUpdate({ style: { ...element.style, spacing: e.target.value } })}
                onKeyDown={(e) => {
                  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                  e.preventDefault();

                  const currentValue = element.style?.spacing || '8px';
                  const match = currentValue.match(/^(-?\d*\.?\d+)(.*)$/);

                  if (!match) {
                    const newValue = e.key === 'ArrowUp' ? '1px' : '0px';
                    onUpdate({ style: { ...element.style, spacing: newValue } });
                    return;
                  }

                  const numValue = parseFloat(match[1]) || 0;
                  const unit = match[2] || 'px';
                  let increment = 1;
                  if (e.shiftKey) increment = 10;
                  else if (e.altKey) increment = 0.1;

                  const newValue = e.key === 'ArrowUp' ? numValue + increment : numValue - increment;
                  const roundedValue = e.altKey ? newValue.toFixed(1) : Math.round(newValue);
                  onUpdate({ style: { ...element.style, spacing: `${roundedValue}${unit}` } });
                }}
                placeholder="8px"
              />
            </div>
            <div className="form-group">
              <label>Alignement</label>
              <div className="alignment-buttons">
                <button
                  type="button"
                  className={`align-btn ${(element.style?.align || 'flex-start') === 'flex-start' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, align: 'flex-start' } })}
                  title="Aligner à gauche"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="2" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="2" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className={`align-btn ${(element.style?.align || 'flex-start') === 'center' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, align: 'center' } })}
                  title="Centrer"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="4" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="5" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className={`align-btn ${(element.style?.align || 'flex-start') === 'flex-end' ? 'active' : ''}`}
                  onClick={() => onUpdate({ style: { ...element.style, align: 'flex-end' } })}
                  title="Aligner à droite"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="6" y="4" width="12" height="2" rx="1"/>
                    <rect x="2" y="9" width="16" height="2" rx="1"/>
                    <rect x="8" y="14" width="10" height="2" rx="1"/>
                  </svg>
                </button>
              </div>
            </div>
            <MarginControl
              margins={{
                marginTop: element.style?.marginTop,
                marginRight: element.style?.marginRight,
                marginBottom: element.style?.marginBottom,
                marginLeft: element.style?.marginLeft
              }}
              onChange={(margins) => onUpdate({ style: { ...element.style, ...margins } })}
            />
          </>
        );

      case 'qrcode':
        return (
          <>
            <div className="form-group">
              <label>Données (URL, texte, vCard...)</label>
              <textarea
                value={element.qrData || element.value}
                onChange={(e) => onUpdate({ qrData: e.target.value })}
                placeholder="https://example.com"
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>Taille (px)</label>
              <select
                value={element.style?.size || '128'}
                onChange={(e) => onUpdate({ style: { ...element.style, size: e.target.value } })}
              >
                {['80', '96', '128', '160', '192', '256'].map(size => (
                  <option key={size} value={size}>{size}px</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <ColorPickerWithPresets
                label="Couleur du QR Code"
                value={element.style?.fgColor || '#000000'}
                onChange={(color) => onUpdate({ style: { ...element.style, fgColor: color } })}
              />
            </div>
            <div className="form-group">
              <ColorPickerWithPresets
                label="Couleur de fond"
                value={element.style?.bgColor || '#ffffff'}
                onChange={(color) => onUpdate({ style: { ...element.style, bgColor: color } })}
              />
            </div>
            <div className="form-group">
              <label>Alignement</label>
              <select
                value={element.style?.align || 'center'}
                onChange={(e) => onUpdate({ style: { ...element.style, align: e.target.value } })}
              >
                <option value="left">Gauche</option>
                <option value="center">Centre</option>
                <option value="right">Droite</option>
              </select>
            </div>
            <MarginControl
              margins={{
                marginTop: element.style?.marginTop,
                marginRight: element.style?.marginRight,
                marginBottom: element.style?.marginBottom,
                marginLeft: element.style?.marginLeft
              }}
              onChange={(margins) => onUpdate({ style: { ...element.style, ...margins } })}
            />
          </>
        );

      default:
        return <p>Propriétés pour {element.type}</p>;
    }
  };

  const handleIconSelect = (icon) => {
    onUpdate({
      libraryIcon: icon,
      iconType: 'library'
    });
  };

  const handleMediaSelect = (media) => {
    // Construire l'URL complète à partir du file_path
    const imageUrl = config.getUploadUrl(media.file_path);

    // Mettre à jour l'URL de l'image et le texte alternatif
    onUpdate({
      value: imageUrl,
      alt: media.alt_text || media.filename,
      mediaFileName: media.filename
    });
    setShowMediaLibrary(false);

    if (showNotification) {
      showNotification(`Image "${media.filename}" insérée`, 'success');
    }
  };

  return (
    <div>
      <h2>✏️ {getElementTypeLabel(element.type)}</h2>
      {renderProperties()}

      {/* Modal IconPicker */}
      <IconPicker
        isOpen={showIconPicker}
        onClose={() => setShowIconPicker(false)}
        onSelect={handleIconSelect}
        currentIcon={element.libraryIcon?.name}
        currentStyle={element.libraryIcon?.style}
      />

      {/* Modal Media Library */}
      <MediaLibraryModal
        isOpen={showMediaLibrary}
        onClose={() => setShowMediaLibrary(false)}
        onSelectImage={handleMediaSelect}
      />
    </div>
  );
}

export default PropertiesPanel;
